import app from 'flarum/forum/app';
import { extend as extendMethod, override } from 'flarum/common/extend';
import HeaderPrimary from 'flarum/forum/components/HeaderPrimary';
import HeaderSecondary from 'flarum/forum/components/HeaderSecondary';
import LinkButton from 'flarum/common/components/LinkButton';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import classList from 'flarum/common/utils/classList';
import humanTime from 'flarum/common/helpers/humanTime';
import TagsPage from 'ext:flarum/tags/forum/components/TagsPage';
import TagHero from 'ext:flarum/tags/forum/components/TagHero';
import tagIcon from 'ext:flarum/tags/common/helpers/tagIcon';
import sortTags from 'ext:flarum/tags/common/utils/sortTags';
import Footer from 'flarum/forum/components/Footer';
import PxSidebar from './components/PxSidebar';
import PxFooter from './components/PxFooter';
import { headerLinks } from './pixmmoLinks';
import extendCategories from './categories';
import extendWidgets from './widgets';
import { cssUrl, tagAnimation, tagBackgroundUrl, tagIcon as tagPixelIcon, tagModelExtenders, tagStyleVars } from '../common/tagImages';
import { animationFor, animationHostClass, iconFor } from '../common/pixelArt';
export { PxSidebar, PxFooter };
export const extend = [...tagModelExtenders];
function sidebarEnabled() {
  return !!app.session.user || app.forum.attribute('pxShowSideNavToGuests') !== false;
}
app.initializers.add('soup-97-pixmmo-pixelui', () => {
  // ------------------------------------------------------------------
  // Fixed left sidebar, mounted next to the header drawer.

  // ForumApplication isn't in the export registry, so patch the running app instance.
  // Initializers run before boot() calls this.mount(), so the patch is in place in time.
  extendMethod(app, 'mount', () => {
    if (!sidebarEnabled()) return;
    const appEl = document.getElementById('app');
    const drawer = document.getElementById('drawer');
    if (!appEl || !drawer) return;
    const sidebar = document.createElement('aside');
    sidebar.className = 'App-sidebar PxSidebar';
    drawer.after(sidebar);
    appEl.classList.add('Px--withSidebar');
    m.mount(sidebar, PxSidebar);
  });

  // ------------------------------------------------------------------
  // PixMMO's site footer (links and legal pages) in core's empty footer slot.

  override(Footer.prototype, 'view', function (original) {
    return app.forum.attribute('pxShowFooter') === false ? original() : m(PxFooter, null);
  });

  // ------------------------------------------------------------------
  // A Forum link to the forum's home page, then links to the PixMMO
  // website and Discord next to the forum title. Core's header moves
  // whatever doesn't fit into an overflow menu.

  extendMethod(HeaderPrimary.prototype, 'items', items => {
    if (app.forum.attribute('pxShowHeaderLinks') === false) return;
    items.add('pixmmo-forum', m(LinkButton, {
      className: "Button Button--link PxHeaderLink",
      icon: "fas fa-comments",
      href: app.forum.attribute('basePath') + '/'
    }, app.translator.trans('soup-97-pixmmo-pixelui.forum.header.forum')), 51);
    headerLinks().forEach((_ref, i) => {
      let {
        key,
        icon,
        href
      } = _ref;
      items.add(`pixmmo-${key}`, m(LinkButton, {
        className: "Button Button--link PxHeaderLink",
        icon: icon,
        href: href,
        external: true,
        target: "_blank",
        rel: "noopener noreferrer"
      }, app.translator.trans(`soup-97-pixmmo-pixelui.forum.header.${key}`)), 50 - i);
    });
  });

  // ------------------------------------------------------------------
  // "Start a Discussion" moves into the header on desktop.

  extendMethod(HeaderSecondary.prototype, 'items', items => {
    const canStartDiscussion = app.forum.attribute('canStartDiscussion') || !app.session.user;
    items.add('newDiscussion', m(Button, {
      icon: "fas fa-edit",
      className: "Button Button--primary PxHeader-newDiscussion",
      disabled: !canStartDiscussion,
      onclick: () => IndexSidebar.prototype.newDiscussionAction().catch(() => {})
    }, app.translator.trans(`core.forum.index.${canStartDiscussion ? 'start_discussion_button' : 'cannot_start_discussion_button'}`)), 19);
  });

  // ------------------------------------------------------------------
  // The in-page side nav only appears on tag pages (on desktop), where it
  // lists the current tag's family. Elsewhere the fixed sidebar replaces it.
  // On phones and tablets it is left alone, as it doubles as the title control.

  extendMethod(IndexSidebar.prototype, 'view', function (vdom) {
    if (!vdom || !vdom.attrs) return;
    vdom.attrs.className = classList(vdom.attrs.className, app.currentTag?.() ? 'PxTagNav' : 'sideNav--hidden');
  });
  extendMethod(IndexSidebar.prototype, 'navItems', function (items) {
    // The fixed sidebar instantiates IndexSidebar without attrs; it filters tags itself.
    if (!this.attrs) return;
    const current = app.currentTag?.();
    if (!current) return;
    const family = new Set([current, current.parent()].filter(Boolean));
    app.store.all('tags').forEach(tag => {
      if (tag.position() === null) return;
      const inFamily = family.has(tag) || tag.isChild() && family.has(tag.parent());
      if (!inFamily && items.has('tag' + tag.id())) items.remove('tag' + tag.id());
    });
  });

  // ------------------------------------------------------------------
  // Sticky / locked discussions get their own look in the list.

  extendMethod(DiscussionListItem.prototype, 'elementAttrs', function (attrs) {
    const discussion = this.attrs.discussion;
    attrs.className = classList(attrs.className, {
      'DiscussionListItem--sticky': discussion.attribute('isSticky'),
      'DiscussionListItem--locked': discussion.attribute('isLocked')
    });
  });

  // ------------------------------------------------------------------
  // Tag images (Admin → Tags): background on tag heroes, and FoF Categories.

  override(TagHero.prototype, 'className', function (original) {
    const tag = this.attrs.model;
    return classList(original(), animationHostClass(tagAnimation(tag)), {
      'PxHero--hasBackground': tagBackgroundUrl(tag)
    });
  });
  extendMethod(TagHero.prototype, 'viewItems', function (items) {
    const animation = animationFor(tagAnimation(this.attrs.model));
    if (animation) items.add('pxAnimation', animation, 1000);
  });
  override(TagHero.prototype, 'style', function (original) {
    const background = tagBackgroundUrl(this.attrs.model);
    const style = original() || {};

    // Text sits on a dark overlay, so it is always light.
    return background ? {
      ...style,
      '--px-cat-bg': cssUrl(background),
      '--contrast-color': '#fafafa'
    } : style;
  });
  extendCategories();

  // ------------------------------------------------------------------
  // Tags page: pixel-framed tag cards.

  override(TagsPage.prototype, 'tagTileListView', function (original, pinned) {
    return m("ul", {
      className: "PxTagTiles"
    }, pinned.map(this.tagTileView.bind(this)));
  });
  override(TagsPage.prototype, 'tagTileView', function (original, tag) {
    const lastPostedDiscussion = tag.lastPostedDiscussion();
    const children = sortTags(tag.children() || []);
    const pixelIcon = iconFor(tagPixelIcon(tag), 'PxTagCard-pixelIcon');
    return m("li", {
      className: classList('PxTagCard', animationHostClass(tagAnimation(tag)), {
        colored: tag.color(),
        'PxTagCard--hasBackground': tagBackgroundUrl(tag)
      }),
      style: {
        '--tag-color': tag.color() || 'var(--control-bg)',
        ...tagStyleVars(tag)
      }
    }, animationFor(tagAnimation(tag)), m("div", {
      className: "PxTagCard-main"
    }, pixelIcon ? m("div", {
      className: "PxTagCard-icon"
    }, pixelIcon) : tag.icon() && m("div", {
      className: "PxTagCard-icon"
    }, tagIcon(tag, {
      className: 'fa-3x'
    }, {
      useColor: false
    })), m("div", {
      className: "PxTagCard-info"
    }, m(Link, {
      className: "PxTagCard-title",
      href: app.route.tag(tag)
    }, tag.name()), tag.description() && m("p", {
      className: "PxTagCard-description"
    }, tag.description()), children.length > 0 && m("div", {
      className: "PxTagCard-children"
    }, children.map(child => [m(Link, {
      href: app.route.tag(child)
    }, child.name()), ' '])))), lastPostedDiscussion && m(Link, {
      className: "PxTagCard-lastPostedDiscussion",
      href: app.route.discussion(lastPostedDiscussion, lastPostedDiscussion.lastPostNumber())
    }, m("span", {
      className: "PxTagCard-lastPostedDiscussion-title"
    }, lastPostedDiscussion.title()), humanTime(lastPostedDiscussion.lastPostedAt())));
  });
});

// After FoF Categories' initializer, which removes the sidebar widgets.
app.initializers.add('soup-97-pixmmo-pixelui-widgets', extendWidgets, -100);